import { useCallback, useEffect, useRef } from "react";
import { type OnChangeFn, type RowSelectionState } from "@tanstack/react-table";

import { createSelectionColumn } from "../utils/createSelectionColumn";

type UseTableSelectionProps<TData> = {
  data: TData[];
  enableRowSelection?: boolean;
  selectedRows?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getCanSelect?: (row: TData) => boolean;
  compactCheckbox?: boolean;
  hideUnselectableCheckboxes?: boolean;
  disableResetSelectionOnDataChange?: boolean;
};

export const useTableSelection = <TData>({
  data,
  enableRowSelection = false,
  selectedRows,
  onRowSelectionChange,
  getCanSelect,
  compactCheckbox,
  hideUnselectableCheckboxes = false,
  disableResetSelectionOnDataChange,
}: UseTableSelectionProps<TData>) => {
  const prevDataRef = useRef(data);

  useEffect(() => {
    // TODO: tech debt. This is added so it disables the resetting behaviour which caused some bugs in Co-investments, while retaining this behaviour in other places: https://vegainvestments.slack.com/archives/C06QSCQPB37/p1771493075359129
    if (disableResetSelectionOnDataChange) return;

    if (
      enableRowSelection &&
      onRowSelectionChange &&
      prevDataRef.current !== data &&
      data.length >= 1
    ) {
      onRowSelectionChange({});
    }
    prevDataRef.current = data;
  }, [
    data,
    enableRowSelection,
    onRowSelectionChange,
    disableResetSelectionOnDataChange,
  ]);

  const selectionColumn = enableRowSelection
    ? createSelectionColumn(
        getCanSelect,
        onRowSelectionChange,
        compactCheckbox,
        hideUnselectableCheckboxes,
      )
    : null;

  const getSelectedData = useCallback(
    (rowSelection: RowSelectionState = selectedRows || {}) =>
      data.filter((_, index) => Boolean(rowSelection[index.toString()])),
    [data, selectedRows],
  );

  const clearSelection = useCallback(() => {
    if (onRowSelectionChange) {
      onRowSelectionChange({});
    }
  }, [onRowSelectionChange]);

  const selectAll = useCallback(() => {
    if (onRowSelectionChange) {
      const newSelection: RowSelectionState = {};
      data.forEach((row, index) => {
        const canSelect = getCanSelect ? getCanSelect(row) : true;

        if (canSelect) {
          newSelection[index.toString()] = true;
        }
      });
      onRowSelectionChange(newSelection);
    }
  }, [data, getCanSelect, onRowSelectionChange]);

  const selectedCount = selectedRows
    ? Object.values(selectedRows).filter(Boolean).length
    : 0;

  const selectableCount = getCanSelect
    ? data.filter(getCanSelect).length
    : data.length;

  return {
    selectionColumn,
    getSelectedData,
    clearSelection,
    selectAll,
    selectedCount,
    selectableCount,
    hasSelection: selectedCount > 0,
    isAllSelected: selectedCount > 0 && selectedCount === selectableCount,
  };
};
